import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import { Close, Inventory, Save } from "@mui/icons-material";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import { refillMedication } from "../../../../../../api/services/medication";
import { QK } from "../../../../../../base/qk";
import CustomInput from "../../../../../FormFields/CustomInput";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 500 },
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  p: 4,
};

const RefillMedicationModal = ({
  open,
  handleClose,
  medication,
  clientID,
}) => {
  const [refillData, setRefillData] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!medication || !open) {
      setRefillData([]);
      return;
    }

    if (medication.medicine_type === "Blister Pack") {
      setRefillData([
        {
          name: "Total Slots",
          current: Number(medication.total_slots_remaining || 0),
          refill: "",
        },
      ]);
    } else {
      setRefillData(
        (medication.individual_meds || []).map((med) => ({
          id: med.id,
          name: med.name,
          current: Number(med.remaining_stock || 0),
          refill: "",
        }))
      );
    }
  }, [medication, open]);

  const { mutate, isLoading } = useMutation(
    (payload) => refillMedication(medication.id, payload),
    {
      onSuccess: () => {
        enqueueSnackbar(
          "Stock refilled and ledger updated successfully!",
          { variant: "success" }
        );

        queryClient.invalidateQueries([
          QK.clientMedications,
          clientID,
        ]);

        queryClient.invalidateQueries([
          QK.medicationrefillhistory,
          medication.id,
        ]);

        setShowConfirm(false);
        handleClose();
      },

      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.detail ||
            "Failed to refill stock.",
          { variant: "error" }
        );

        setShowConfirm(false);
      },
    }
  );

  const handleChange = (index, value) => {
    if (value !== "" && Number(value) < 0) {
      return;
    }

    const newData = [...refillData];

    newData[index] = {
      ...newData[index],
      refill: value,
    };

    setRefillData(newData);
  };

  const hasRefillQuantity = refillData.some(
    (item) => Number(item.refill || 0) > 0
  );

  const handleProcessSubmit = () => {
    if (!medication || !hasRefillQuantity) {
      return;
    }

    const isBlister = medication.medicine_type === "Blister Pack";

    if (isBlister) {
      const current = Number(medication.total_slots_remaining || 0);
      const refill = Number(refillData[0]?.refill || 0);

      mutate({
        medicine_type: medication.medicine_type,
        total_slots_remaining: current + refill,
        individual_meds: medication.individual_meds || [],
      });

      return;
    }

    const individualMeds = (medication.individual_meds || []).map(
      (med, index) => {
        const current = Number(med.remaining_stock || 0);
        const refill = Number(refillData[index]?.refill || 0);
        const newStock = current + refill;

        return {
          ...med,
          remaining_stock: newStock,
          initial_stock_qty: newStock,
        };
      }
    );

    mutate({
      medicine_type: medication.medicine_type,
      total_slots_remaining: medication.total_slots_remaining,
      individual_meds: individualMeds,
    });
  };

  if (!medication) {
    return null;
  }

  return (
    <>
      <Modal
        open={open}
        onClose={() => !isLoading && handleClose()}
      >
        <Box sx={style}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Inventory color="primary" />

              <Typography variant="h6" fontWeight={900}>
                Refill Stock
              </Typography>
            </Stack>

            <IconButton
              onClick={handleClose}
              size="small"
              disabled={isLoading}
            >
              <Close />
            </IconButton>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Medication
          </Typography>

          <Typography variant="body1" fontWeight={700} sx={{ mb: 3 }}>
            {medication.title || medication.name || "Medication"}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Stack spacing={2.5}>
            {refillData.map((item, index) => {
              const current = Number(item.current || 0);
              const refill = Number(item.refill || 0);
              const newTotal = current + refill;

              return (
                <Box
                  key={item.id || item.name || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Current: {current}
                    </Typography>

                    {refill > 0 && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          color: "#059669",
                          fontWeight: 700,
                          mt: 0.25,
                        }}
                      >
                        New Total: {newTotal}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ width: 150 }}>
                    <CustomInput
                      type="number"
                      label="Add Qty"
                      value={item.refill}
                      onChange={(e) => handleChange(index, e.target.value)}
                      disabled={isLoading}
                      inputProps={{ min: 0 }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {refillData.length === 0 && (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No medication stock found.
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<Save />}
              onClick={() => setShowConfirm(true)}
              disabled={
                isLoading ||
                !hasRefillQuantity ||
                refillData.length === 0
              }
              sx={{
                bgcolor: "#059669",
                "&:hover": { bgcolor: "#047857" },
              }}
            >
              Confirm Refill
            </Button>
          </Box>
        </Box>
      </Modal>

      <ConfirmModal
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={handleProcessSubmit}
        isLoading={isLoading}
        title="Confirm Stock Update"
        body={`Are you sure you want to add the specified quantity to ${
          medication.title || medication.name || "this medication"
        }? This action will be recorded in the stock ledger.`}
        confirmText="Yes, Update Stock"
      />
    </>
  );
};

export default RefillMedicationModal;